import {
    CART_STATUS,
    CART_STATUS_COLORS,
    DEFAULT_TELEMETRY_INTERVAL_MINUTES,
    EMPTY_STRING_PLACEHOLDER,
    TELEMETRY_ABSOLUTE_CUTOFF_DAYS,
    TELEMETRY_INTERVAL_COLUMN,
    TELEMETRY_OFFLINE_INTERVAL_MULTIPLIER,
    TELEMETRY_TIMESTAMP_COLUMN
} from "./Constants";

const MILLIS_PER_MINUTE = 60000;
const MILLIS_PER_HOUR = 3600000;
const MILLIS_PER_DAY = 86400000;

/**
 * Epoch millis of the last telemetry frame we received for a cart, or null when the cart has never
 * reported. NARC merges attribute maps rather than replacing them, so a cart that stops reporting
 * keeps serving its last known values indefinitely -- the absence of this key means "never seen",
 * not "recently quiet".
 */
function getTelemetryEpoch(attributes) {
    const raw = (attributes || {})[TELEMETRY_TIMESTAMP_COLUMN];
    if (!raw) return null;
    const epoch = Date.parse(raw);
    return isNaN(epoch) ? null : epoch;
}

/**
 * Reporting cadence in minutes as declared by the device, falling back to a default for records
 * written before telemetryTimestamp existed.
 */
function getTelemetryIntervalMinutes(attributes) {
    const parsed = parseInt((attributes || {})[TELEMETRY_INTERVAL_COLUMN], 10);
    return isNaN(parsed) || parsed <= 0 ? DEFAULT_TELEMETRY_INTERVAL_MINUTES : parsed;
}

/**
 * Age of the last telemetry frame in millis, or null when the cart has never reported. Clamped at
 * zero: "now" comes from the operator's browser while the timestamp comes from the thin client, so
 * clock skew can otherwise produce a negative age.
 */
function getTelemetryAgeMillis(attributes, now = Date.now()) {
    const epoch = getTelemetryEpoch(attributes);
    if (epoch === null) return null;
    return Math.max(0, now - epoch);
}

/**
 * Derives online/offline/unknown from telemetry age. Offline is the absence of an expected frame,
 * so it can only be established at read time -- no upstream service can publish it.
 */
function getCartStatus(attributes, now = Date.now()) {
    const age = getTelemetryAgeMillis(attributes, now);
    if (age === null) return CART_STATUS.UNKNOWN;
    const threshold = getTelemetryIntervalMinutes(attributes)
        * TELEMETRY_OFFLINE_INTERVAL_MULTIPLIER * MILLIS_PER_MINUTE;
    return age <= threshold ? CART_STATUS.ONLINE : CART_STATUS.OFFLINE;
}

/**
 * Compact relative age for the table cell. Buckets are tuned for what is actually on screen: an
 * online cart is minutes old, and anything past a couple of weeks reads better as a date.
 */
function formatTelemetryAge(attributes, now = Date.now()) {
    const age = getTelemetryAgeMillis(attributes, now);
    if (age === null) return EMPTY_STRING_PLACEHOLDER;
    if (age < MILLIS_PER_MINUTE) return "just now";
    if (age < MILLIS_PER_HOUR) return `${Math.floor(age / MILLIS_PER_MINUTE)} min ago`;
    if (age < MILLIS_PER_DAY) return `${Math.floor(age / MILLIS_PER_HOUR)} h ago`;
    const days = Math.floor(age / MILLIS_PER_DAY);
    if (days < TELEMETRY_ABSOLUTE_CUTOFF_DAYS) return `${days} d ago`;
    return new Date(getTelemetryEpoch(attributes)).toISOString().split("T")[0];
}

/**
 * Tooltip text behind the relative age: local time for the operator, UTC ISO for tickets.
 */
function formatTelemetryTimestamp(attributes) {
    const epoch = getTelemetryEpoch(attributes);
    if (epoch === null) return "No telemetry received from this cart";
    const date = new Date(epoch);
    return `${date.toLocaleString()} (local) | ${date.toISOString()}`;
}

/**
 * Raw ISO timestamp for CSV export. The download is for analysis, so it never carries a relative
 * label.
 */
function getTelemetryTimestampForExport(attributes) {
    return (attributes || {})[TELEMETRY_TIMESTAMP_COLUMN] || "";
}

/**
 * Text colour for the status cell. Deliberately not reusing getConnectivityBadgeStyle from
 * LidarTesterUtil: that helper is binary and renders anything other than "connected" as an error,
 * which would make unknown indistinguishable from offline.
 */
function getCartStatusColor(status) {
    return CART_STATUS_COLORS[status] || CART_STATUS_COLORS[CART_STATUS.UNKNOWN];
}

export {
    getTelemetryEpoch,
    getTelemetryIntervalMinutes,
    getTelemetryAgeMillis,
    getCartStatus,
    formatTelemetryAge,
    formatTelemetryTimestamp,
    getTelemetryTimestampForExport,
    getCartStatusColor
};



// WEBPACK FOOTER //
// ./src/components/util/CartTelemetryUtils.js